import { apiService } from '../api';
import { CurriculumSubject } from '../../types/sessions';

export interface ListCurriculumFilter {
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string | null;   // null = common-only
}

export interface AssignCurriculumInput {
  sessionId: string;
  classId: string;
  sectionInstanceId?: string | null;
  subjectId: string;
  // Optional — the server defaults to 5 if omitted.
  periodsPerWeek?: number;
}

export interface BulkAssignCurriculumInput {
  assignments: AssignCurriculumInput[];
}

export interface CopyFromSessionInput {
  sourceSessionId: string;
  targetSessionId: string;
  classIds?: string[];
}

const buildListParams = (filter: ListCurriculumFilter) => {
  const params: Record<string, unknown> = {};
  if (filter.sessionId) params.sessionId = filter.sessionId;
  if (filter.classId)   params.classId   = filter.classId;
  if (filter.sectionInstanceId === null)                params.sectionInstanceId = 'null';
  else if (typeof filter.sectionInstanceId === 'string') params.sectionInstanceId = filter.sectionInstanceId;
  return params;
};

export const curriculumApi = {
  list: async (filter: ListCurriculumFilter = {}) => {
    const response = await apiService.get<CurriculumSubject[]>('/curriculum-subjects', buildListParams(filter));
    return response.data ?? [];
  },

  // Effective candidate subjects for a class-level exam in (session, class).
  // Server-side merges common + section-specific rows, dedupes, and populates
  // names so the modal can drop them straight into the picker.
  listForExam: async (params: { sessionId: string; classId: string }) => {
    const response = await apiService.get<Array<{ _id: string; name: string }>>(
      '/curriculum-subjects/for-exam',
      params
    );
    return response.data ?? [];
  },

  resolveForEnrollment: async (enrollmentId: string) => {
    const response = await apiService.get<CurriculumSubject[]>('/curriculum-subjects/resolve', { enrollmentId });
    return response.data ?? [];
  },

  assign: async (input: AssignCurriculumInput) => {
    const response = await apiService.post<CurriculumSubject>('/curriculum-subjects', input);
    return response.data!;
  },

  bulkAssign: async (input: BulkAssignCurriculumInput) => {
    const response = await apiService.post<{ created: number }>('/curriculum-subjects/bulk', input);
    return response.data!;
  },

  copyFromSession: async (input: CopyFromSessionInput) => {
    const response = await apiService.post<{ created: number; skipped: number; unmatched: number }>(
      '/curriculum-subjects/copy-from-session',
      input
    );
    return response.data!;
  },

  remove: async (id: string) => {
    await apiService.delete(`/curriculum-subjects/${id}`);
  }
};
